<script setup lang="ts">
import { type ComboboxGroup, type ComboboxOption } from '@/ui/combobox'

import type { Audience } from '@/types/types'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { Icons } from '@/components/base/BaseIcon.vue'

interface AudienceOption extends ComboboxOption<number> {
  badgeColor?: ChipColor
  badgeIcon?: Icons
}

const { t } = useI18n()
const { segmentTypes, createAudience } = useAudience()
const notification = useNotification()

const props = withDefaults(
  defineProps<{
    audiences: Audience[]
    disabled?: boolean
    clearable?: boolean
    creationAllowed?: boolean
    defaultEmptyMessage?: string
    filterPlaceholder?: string
    modelValue: number | null | undefined
    placeholder?: string
    type?: 'static' | 'dynamic' | 'typology' | null
  }>(),
  {
    clearable: false,
    creationAllowed: false,
    disabled: false
  }
)

const emit = defineEmits(['update:modelValue', 'audience-created'])

const selectedValue = computed<number | null>({
  get: () => (props.modelValue != null ? props.modelValue : null),
  set: (v) => emit('update:modelValue', v)
})

const isCreatingAudience = ref(false)

const createNewAudience = async (name: string) => {
  const trimmed = name.trim()
  if (!trimmed) return

  try {
    isCreatingAudience.value = true

    const newAudience: Audience = {
      name: trimmed,
      type: 'static'
    }

    const audienceId = await createAudience({ audience: newAudience })

    if (audienceId) {
      emit('audience-created')
      emit('update:modelValue', audienceId)
    }
  } catch (error) {
    notification.create('error', {
      text: t('pages.customer.audiences.create.notificationError', { errorMessage: (error as Error).message }),
      timeout: 3000
    })
  } finally {
    isCreatingAudience.value = false
  }
}

const sortAlphabetically = <T extends { label: string }>(items: T[]) =>
  items.slice().sort((a, b) => a.label.localeCompare(b.label))

const allAudienceOptions = computed<AudienceOption[]>(() =>
  props.audiences.map((audience) => {
    const type = audience.frequency_type ? 'typology' : audience.type
    const segment = segmentTypes.find((s) => s.id === type)
    return {
      value: audience.id,
      label: audience.name,
      badgeColor: segment?.color,
      badgeIcon: segment?.icon
    }
  })
)

const groups = computed<ComboboxGroup<number>[]>(() => {
  const byType = (type: 'typology' | 'dynamic' | 'static') =>
    sortAlphabetically(
      allAudienceOptions.value.filter((o) => {
        const source = props.audiences.find((a) => a.id === o.value)
        const actual = source?.frequency_type ? 'typology' : source?.type
        return actual === type
      })
    )

  const result: ComboboxGroup<number>[] = []
  if (!props.type || props.type === 'typology') {
    result.push({ heading: t('global.multiselect.audiences.typology'), items: byType('typology') })
  }
  if (!props.type || props.type === 'dynamic') {
    result.push({ heading: t('global.multiselect.audiences.dynamic'), items: byType('dynamic') })
  }
  if (!props.type || props.type === 'static') {
    result.push({ heading: t('global.multiselect.audiences.static'), items: byType('static') })
  }
  return result.filter((g) => g.items.length > 0)
})
</script>

<template>
  <ComboboxSelect
    v-model="selectedValue"
    :groups="groups"
    :disabled="disabled"
    :clearable="clearable"
    :placeholder="placeholder ?? ''"
    :search-placeholder="filterPlaceholder ?? t('global.multiselect.audiences.segmentsSearchPlaceholder')"
    :empty-message="defaultEmptyMessage ?? t('global.multiselect.audiences.noResultSearch')"
    :search-threshold="0"
  >
    <template #trigger="{ selected, placeholder: fallbackPlaceholder }">
      <div v-if="selected" class="flex min-w-0 flex-1 items-center">
        <BaseChip
          :color="(selected as AudienceOption).badgeColor"
          :icon="(selected as AudienceOption).badgeIcon"
          :text="selected.label"
        />
      </div>
      <span v-else class="truncate text-sm text-foreground-secondary">{{ fallbackPlaceholder }}</span>
    </template>

    <template #option="{ option }">
      <BaseChip
        :color="(option as AudienceOption).badgeColor"
        :icon="(option as AudienceOption).badgeIcon"
        :text="option.label"
      />
    </template>

    <template #empty="{ search }">
      <button
        v-if="creationAllowed && search.trim()"
        type="button"
        class="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-primary hover:bg-primary/5 disabled:opacity-50"
        :disabled="isCreatingAudience"
        @click="createNewAudience(search)"
      >
        <BaseIcon name="HeroPlusIconSolid" class="h-4 w-4" />
        <span>{{ t('pages.customer.audiences.create.createNewFromFilter', { name: search }) }}</span>
      </button>
      <div v-else class="p-3 text-center text-sm text-muted-foreground">
        {{ defaultEmptyMessage ?? t('global.multiselect.audiences.noResultSearch') }}
      </div>
    </template>
  </ComboboxSelect>
</template>
